/**
 * TKP-234 — "Farkları gör": yayımlanan metin ile güncel envanterden üretilecek metnin kelime bazlı farkı.
 *
 * İki metin de HTML (başlık, tablo, liste). Etiketler karşılaştırılmaz, yerinde aynen bırakılır; yalnız kelimeler
 * karşılaştırılır. Silinen kelime solda `<mark class="diff-del">`, eklenen sağda `<mark class="diff-ins">` içine
 * alınır. Bitişik değişiklikler bir "hunk" oluşturur; her işaret `data-hunk` taşır (sonraki/önceki fark gezinmesi).
 * İşaret hiçbir etiketi kesmez: etiketten önce kapanır, sonra yeniden açılır — tablo yapısı bozulmaz. Saf.
 */

/** Ortak önek/sonek dışında kalan bölüm için LCS tablosu üst sınırı (hücre). Aşılırsa bölüm bütün olarak değişmiş sayılır. */
export const MAX_CELLS = 2500000;

const TOKEN = /(<[^>]*>)|(\s+|&nbsp;|&#160;)|([^<\s&]+|&[^;\s<]*;?)/gi;

/** HTML → { type: "tag" | "space" | "word", value }[] */
export const tokenize = (html) => {
  const out = [];
  const text = html || "";
  let m;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(text))) {
    if (m[1]) out.push({ type: "tag", value: m[1] });
    else if (m[2]) out.push({ type: "space", value: m[2] });
    else out.push({ type: "word", value: m[3] });
  }
  return out;
};

/**
 * İki kelime dizisinin farkı: her kelime için değişti mi (a: silindi, b: eklendi).
 * Ortak önek/sonek kırpılır; kalan bölüm LCS ile, sınır aşılırsa bütün olarak işaretlenir.
 */
export const diffWords = (a, b, maxCells = MAX_CELLS) => {
  const delA = new Array(a.length).fill(false);
  const insB = new Array(b.length).fill(false);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  const n = endA - start;
  const m = endB - start;
  if (!n && !m) return { delA, insB, truncated: false };
  if (!n || !m || n * m > maxCells) {
    for (let i = start; i < endA; i++) delA[i] = true;
    for (let j = start; j < endB; j++) insB[j] = true;
    return { delA, insB, truncated: n * m > maxCells };
  }
  // lcs[i][j] = a[start+i..] ile b[start+j..] arasındaki LCS uzunluğu (tek boyutlu dizi)
  const w = m + 1;
  const lcs = new Uint32Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i * w + j] = a[start + i] === b[start + j] ? lcs[(i + 1) * w + j + 1] + 1 : Math.max(lcs[(i + 1) * w + j], lcs[i * w + j + 1]);
    }
  }
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[start + i] === b[start + j]) {
      i++;
      j++;
    } else if (lcs[(i + 1) * w + j] >= lcs[i * w + j + 1]) delA[start + i++] = true;
    else insB[start + j++] = true;
  }
  while (i < n) delA[start + i++] = true;
  while (j < m) insB[start + j++] = true;
  return { delA, insB, truncated: false };
};

/**
 * Hunk numaraları: iki dizi aynı sırada yürünür; ortak kelimeler arasında kalan her değişiklik bölgesi bir hunk.
 * Döner: { hunkA, hunkB, count } — değişmeyen kelimede -1.
 */
const assignHunks = (delA, insB) => {
  const hunkA = new Array(delA.length).fill(-1);
  const hunkB = new Array(insB.length).fill(-1);
  let i = 0;
  let j = 0;
  let count = 0;
  while (i < delA.length || j < insB.length) {
    if (i < delA.length && j < insB.length && !delA[i] && !insB[j]) {
      i++;
      j++;
      continue;
    }
    let touched = false;
    while (i < delA.length && delA[i]) {
      hunkA[i++] = count;
      touched = true;
    }
    while (j < insB.length && insB[j]) {
      hunkB[j++] = count;
      touched = true;
    }
    if (touched) count++;
    else break; // savunma: eşit olmayan ama işaretsiz durum oluşmamalı
  }
  return { hunkA, hunkB, count };
};

/** Belirteçleri yeniden HTML'e çevirir; değişen kelimeleri işaretler, işaret hiçbir etiketi kesmez. */
const render = (tokens, changed, hunks, cls) => {
  let html = "";
  let open = -1; // açık işaretin hunk numarası
  let pendingSpace = "";
  let w = 0;
  const close = () => {
    if (open >= 0) html += "</mark>";
    open = -1;
  };
  for (const t of tokens) {
    if (t.type === "space") {
      pendingSpace += t.value;
      continue;
    }
    if (t.type === "tag") {
      close();
      html += pendingSpace + t.value;
      pendingSpace = "";
      continue;
    }
    const idx = w++;
    if (changed[idx]) {
      if (open >= 0 && open !== hunks[idx]) close();
      if (open < 0) {
        html += pendingSpace + `<mark class="${cls}" data-hunk="${hunks[idx]}">`;
        open = hunks[idx];
      } else html += pendingSpace;
    } else {
      close();
      html += pendingSpace;
    }
    pendingSpace = "";
    html += t.value;
  }
  close();
  return html + pendingSpace;
};

/**
 * Yayımlanan (stored) ve güncel (current) HTML'in işaretli halleri ve özet.
 * Döner: { stored, current, inserted, deleted, hunks, truncated }
 */
export const diffHtml = (storedHtml, currentHtml, maxCells = MAX_CELLS) => {
  const ta = tokenize(storedHtml);
  const tb = tokenize(currentHtml);
  const a = ta.filter((t) => t.type === "word").map((t) => t.value);
  const b = tb.filter((t) => t.type === "word").map((t) => t.value);
  const { delA, insB, truncated } = diffWords(a, b, maxCells);
  const { hunkA, hunkB, count } = assignHunks(delA, insB);
  return {
    stored: render(ta, delA, hunkA, "diff-del"),
    current: render(tb, insB, hunkB, "diff-ins"),
    deleted: delA.filter(Boolean).length,
    inserted: insB.filter(Boolean).length,
    hunks: count,
    truncated,
  };
};
